/* eslint-disable typescript-extended-rules/file-naming-convention */
import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
	BeneficiaryReconciliationApplyAction,
	BeneficiaryReconciliationApplyTargetValue,
	BeneficiaryReconciliationItem,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';

type ValueResolutionMode = 'CUSTOM' | 'DATABASE' | 'EXTERNAL';

interface BeneficiaryReconciliationValueResolutionSavedResult {
	applyOnly?: boolean;
	mode: ValueResolutionMode;
	onHold?: boolean;
	targetValue: BeneficiaryReconciliationApplyTargetValue;
}

export type BeneficiaryReconciliationValueResolutionResult =
	| { removePending: true }
	| BeneficiaryReconciliationValueResolutionSavedResult;

interface ValueResolutionDialogData {
	action: BeneficiaryReconciliationApplyAction | null;
	item: BeneficiaryReconciliationItem;
	targetValue: BeneficiaryReconciliationApplyTargetValue | null;
}

@Component({
	selector: 'app-beneficiary-reconciliation-value-resolution-modal',
	styleUrls: [
		'./beneficiary-reconciliation-value-resolution-modal.component.scss',
	],
	templateUrl:
		'./beneficiary-reconciliation-value-resolution-modal.component.html',
})
export class BeneficiaryReconciliationValueResolutionModalComponent {
	errorMessage = '';
	form: UntypedFormGroup;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ValueResolutionDialogData,
		private readonly dialogRef: MatDialogRef<BeneficiaryReconciliationValueResolutionModalComponent>,
		private readonly fb: UntypedFormBuilder,
	) {
		const dbValue = (data.item.dbValue ?? {}) as Record<string, unknown>;
		const externalValue = (data.item.externalValue ?? {}) as Record<
			string,
			unknown
		>;
		const targetPlanName =
			data.targetValue?.planName ??
			(typeof externalValue.planName === 'string'
				? externalValue.planName
				: '');
		const targetCardNumber =
			data.targetValue?.cardNumber ??
			(typeof externalValue.cardNumber === 'string'
				? externalValue.cardNumber
				: '');
		const mode = this.resolveInitialMode(
			data.action,
			data.targetValue,
			{
				cardNumber:
					typeof dbValue.cardNumber === 'string' ? dbValue.cardNumber : '',
				planName: typeof dbValue.planName === 'string' ? dbValue.planName : '',
			},
			{
				cardNumber:
					typeof externalValue.cardNumber === 'string'
						? externalValue.cardNumber
						: '',
				planName:
					typeof externalValue.planName === 'string'
						? externalValue.planName
						: '',
			},
		);

		this.form = this.fb.group({
			cardNumber: [targetCardNumber],
			mode: [mode],
			planName: [targetPlanName],
		});
	}

	get beneficiaryContextLabel(): string {
		const name = this.getStringValue(this.data.item.beneficiaryName);
		const cpf = this.getStringValue(this.data.item.beneficiaryCpf);

		if (name && cpf) {
			return `${name} - ${cpf}`;
		}

		return name || cpf;
	}

	get benefitVariantLabel(): string {
		const context = this.data.item.context ?? {};
		const benefitVariant = this.getStringValue(
			context.benefitVariant,
		).toUpperCase();
		const benefitVariantLabel = this.getStringValue(
			context.benefitVariantLabel,
		).toUpperCase();
		const walletType = this.getStringValue(context.walletType).toUpperCase();

		if (
			benefitVariant === 'CONGENER' ||
			benefitVariantLabel === 'CONGÊNERE' ||
			walletType === 'CONGENER'
		) {
			return 'Congênere';
		}

		if (
			benefitVariant === 'CONJUGATED' ||
			benefitVariantLabel === 'CONJUGADO' ||
			walletType === 'CONJUGATED'
		) {
			return 'Conjugado';
		}

		return '';
	}

	get currentDatabaseValue(): string {
		const value = (this.data.item.dbValue ?? {}) as Record<string, unknown>;
		const rawValue = this.isPlanMismatch
			? this.getStringValue(value.planName)
			: this.getStringValue(value.cardNumber);

		if (this.isDentalNotApplicableCandidate && !rawValue) {
			return 'Não aplicável';
		}

		if (this.isPlanMismatch) {
			return rawValue || '-';
		}

		return rawValue || '-';
	}

	get currentExternalValue(): string {
		if (this.isDentalNotApplicableCandidate && !this.externalRawValue) {
			return 'Não aplicável';
		}

		return this.externalRawValue || '-';
	}

	get externalRawValue(): string {
		const value = (this.data.item.externalValue ?? {}) as Record<
			string,
			unknown
		>;

		if (this.isPlanMismatch) {
			return this.getStringValue(value.planName);
		}

		return this.getStringValue(value.cardNumber);
	}

	get hasPendingChange(): boolean {
		return Boolean(this.data.action || this.data.targetValue);
	}

	get isCardMismatch(): boolean {
		return this.data.item.field === 'CARD_MISMATCH';
	}

	get isDentalNotApplicableCandidate(): boolean {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const walletKind = this.getStringValue(context.walletKind).toUpperCase();

		return (
			context.dentalNotApplicableCandidate === true && walletKind === 'DENTAL'
		);
	}

	get isPlanMismatch(): boolean {
		return this.data.item.field === 'PLAN_MISMATCH';
	}

	get mode(): ValueResolutionMode {
		return this.form.get('mode')?.value as ValueResolutionMode;
	}

	get shouldShowCustomField(): boolean {
		return this.mode === 'CUSTOM';
	}

	onApplyOnly(): void {
		this.onSave(true);
	}

	onClose(): void {
		this.dialogRef.close();
	}

	onIgnore(): void {
		this.dialogRef.close({
			mode: 'DATABASE',
			onHold: true,
			targetValue: {},
		} satisfies BeneficiaryReconciliationValueResolutionResult);
	}

	onRemove(): void {
		this.dialogRef.close({ removePending: true });
	}

	onSave(applyOnly = false): void {
		const mode = this.mode;
		const planName = this.getValue('planName');
		const cardNumber = this.getValue('cardNumber');

		if (mode === 'DATABASE') {
			this.dialogRef.close({
				applyOnly: applyOnly || undefined,
				mode,
				targetValue: {},
			} satisfies BeneficiaryReconciliationValueResolutionResult);

			return;
		}

		if (mode === 'EXTERNAL') {
			if (this.isDentalNotApplicableCandidate && !this.externalRawValue) {
				this.dialogRef.close({
					applyOnly: applyOnly || undefined,
					mode,
					targetValue: {
						dentalNotApplicable: true,
					},
				} satisfies BeneficiaryReconciliationValueResolutionResult);

				return;
			}

			this.dialogRef.close({
				applyOnly: applyOnly || undefined,
				mode,
				targetValue: {
					cardNumber: this.isCardMismatch ? this.externalRawValue : undefined,
					planName: this.isPlanMismatch ? this.externalRawValue : undefined,
				},
			} satisfies BeneficiaryReconciliationValueResolutionResult);

			return;
		}

		if (this.isPlanMismatch && !planName) {
			this.errorMessage = 'Informe o nome do plano para continuar.';

			return;
		}

		if (this.isCardMismatch && !cardNumber) {
			this.errorMessage = 'Informe o número da carteirinha para continuar.';

			return;
		}

		this.dialogRef.close({
			applyOnly: applyOnly || undefined,
			mode,
			targetValue: {
				cardNumber: cardNumber || undefined,
				planName: planName || undefined,
			},
		} satisfies BeneficiaryReconciliationValueResolutionResult);
	}

	private getStringValue(value: unknown): string {
		return typeof value === 'string' ? value.trim() : '';
	}

	private getValue(fieldName: 'cardNumber' | 'planName'): string {
		const value = this.form.get(fieldName)?.value;

		return this.getStringValue(value);
	}

	private resolveInitialMode(
		action: BeneficiaryReconciliationApplyAction | null,
		targetValue: BeneficiaryReconciliationApplyTargetValue | null,
		dbValue: { cardNumber: string; planName: string },
		externalValue: { cardNumber: string; planName: string },
	): ValueResolutionMode {
		if (action === 'IGNORE_ITEM') {
			return 'DATABASE';
		}

		if (!targetValue) {
			return 'EXTERNAL';
		}

		if (targetValue.dentalNotApplicable === true) {
			return 'EXTERNAL';
		}

		const targetCurrentValue = this.isPlanMismatch
			? this.getStringValue(targetValue.planName)
			: this.getStringValue(targetValue.cardNumber);
		const currentDbValue = this.isPlanMismatch
			? dbValue.planName
			: dbValue.cardNumber;
		const currentExternalValue = this.isPlanMismatch
			? externalValue.planName
			: externalValue.cardNumber;

		if (targetCurrentValue && targetCurrentValue === currentDbValue) {
			return 'DATABASE';
		}

		if (!targetCurrentValue || targetCurrentValue === currentExternalValue) {
			return 'EXTERNAL';
		}

		return 'CUSTOM';
	}
}
